import React, { useEffect, useMemo } from 'react';
import { useTypewriter } from './useTypewriter';

type LogoProps = {
  variant?: 'idle' | 'wordmark' | 'animated';
  text?: string;
  size?: number | string;
  ariaLabel?: string;
  animationDurationMs?: number;
  typingEnabled?: boolean;
  showCursor?: boolean;
  cursorBlinkMs?: number;
  cursorFadeOutMs?: number;
  loop?: boolean;
};

export const Logo: React.FC<LogoProps> = ({
  variant = 'idle',
  text = 'Consl',
  size = 24,
  ariaLabel,
  animationDurationMs = 3200,
  typingEnabled,
  showCursor = true,
  cursorBlinkMs = 530,
  cursorFadeOutMs = 350,
  loop = false,
}) => {
  const Tag = 'span';
  const isWordmark = variant === 'wordmark' || variant === 'animated';

  const resolvedTypingEnabled = useMemo(() => {
    if (!isWordmark) return false;
    if (typeof typingEnabled === 'boolean') return typingEnabled;
    return variant === 'animated';
  }, [isWordmark, typingEnabled, variant]);

  const fontSize = typeof size === 'number' ? `${size}px` : size;
  const fontWeight = 600;

  const shouldAnimate = resolvedTypingEnabled;

  const [typingKey, setTypingKey] = React.useState(0);
  useEffect(() => {
    setTypingKey((k) => k + 1);
  }, [text]);
  useEffect(() => {
    setTypingKey((k) => k + 1);
  }, [variant]);

  const { displayText, isComplete, cursorVisible, cursorOpacity } = useTypewriter({
    text,
    running: shouldAnimate,
    speedMsPerChar: 100,
    startDelayMs: 150,
    showCursor,
    cursorBlinkMs,
    cursorFadeOutMs,
    loop,
  });



  const enablePulse = useMemo(() => {
    const policy = (variant === 'animated' ? 'pulse' : 'none');
    return policy === 'pulse' && isComplete;
  }, [variant, isComplete]);

  const animationStyle = `
    @keyframes conslLogoFade {
      0%   { background-position: 0% 50%; }
      50%  { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    .consl-logo__text--animated {
      background: linear-gradient(
        90deg,
        var(--color-logo-gradient-start) 0%,
        var(--color-logo-gradient-mid) 50%,
        var(--color-logo-gradient-end) 100%
      );
      background-size: 200% 100%;
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    @media (prefers-reduced-motion: reduce) {
      .consl-logo__text--animated {
        animation: none !important;
        background: none !important;
        -webkit-text-fill-color: inherit;
      }
    }
  `;

  const finalText = shouldAnimate ? displayText : text

  return (
    <>
      <style>{animationStyle}</style>
      <Tag
        key={typingKey}
        className="inline-flex items-baseline text-inherit"
        style={{
          fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
          fontSize,
          lineHeight: 1,
          verticalAlign: 'middle',
        }}
        role={ariaLabel ? 'img' : undefined}
        aria-label={ariaLabel}
        aria-hidden={ariaLabel ? undefined : true}
      >
        <span aria-hidden="true" className="select-none">[</span>
        {isWordmark && (
          <span
            className={enablePulse ? 'consl-logo__text--animated' : undefined}
            style={{
              marginLeft: 4,
              marginRight: 4,
              fontWeight,
              animation: enablePulse ? `conslLogoFade ${animationDurationMs}ms ease-in-out infinite` : undefined,
              whiteSpace: 'pre',
              display: 'inline-flex',
              alignItems: 'baseline',
            }}
          >
            <span>{finalText}</span>
            {showCursor && shouldAnimate && !isComplete && (
              <span
                aria-hidden="true"
                style={{
                  width: '0.12em',
                  height: '0.75em',
                  backgroundColor: 'currentColor',
                  display: 'inline-block',
                  marginLeft: '0.08em',
                  opacity: cursorVisible ? 1 : 0,
                  transition: 'opacity 150ms linear',
                  alignSelf: 'center',
                }}
              />
            )}
            {showCursor && shouldAnimate && isComplete && cursorOpacity > 0 && (
              <span
                aria-hidden="true"
                style={{
                  width: '0.12em',
                  height: '0.75em',
                  backgroundColor: 'currentColor',
                  display: 'inline-block',
                  marginLeft: '0.08em',
                  opacity: cursorOpacity,
                  alignSelf: 'center',
                }}
              />
            )}
          </span>
        )}
        <span aria-hidden="true" className="select-none">]</span>
      </Tag>
    </>
  );
};

export default Logo;
